/**
 * OsAuditDashboard — OS-INV-22
 *
 * Route: /os/audit (Firebase-staff-protected)
 *
 * Displays two states driven by GET /api/os/audit/cycles/active:
 *   1. No-cycle state (404): start-cycle card with editable cycle name.
 *   2. Active-cycle state (200): cycle header bar, coverage ring, stats row,
 *      class breakdown cards, overdue items list, and navigation buttons.
 *
 * API calls:
 *   GET  /api/os/audit/cycles/active    — active cycle + progress
 *   GET  /api/os/audit/summary          — by-class totals
 *   GET  /api/os/audit/schedule         — top 10 overdue items (urgency-sorted)
 *   POST /api/os/audit/cycles           — start a cycle
 *   PUT  /api/os/audit/cycles/:id/close — close a cycle
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../madebb/utils/authFetch';
import { OsCountSessionList, type CountSession } from './OsCountSessionList';
import { OsCountSessionDetail } from './OsCountSessionDetail';

// ── Types ──────────────────────────────────────────────────────────────────

interface AuditCycleProgress {
  total_tracked: number;
  total_due: number;
  overdue: number;
  counted_this_cycle: number;
  percent_complete: number;
  weeks_elapsed: number;
}

interface AuditCycle {
  id: string;
  name: string;
  started_at: string;
  closed_at: string | null;
  progress: AuditCycleProgress;
}

interface ClassStats {
  total: number;
  due: number;
  overdue: number;
}

interface AuditSummary {
  total_tracked: number;
  total_due: number;
  total_overdue: number;
  by_class: {
    A: ClassStats;
    B: ClassStats;
    C: ClassStats;
  };
}

interface ScheduleItem {
  material_id: string;
  material_name: string;
  location: string;
  audit_class: 'A' | 'B' | 'C';
  next_due: string;
}

type ToastState = { message: string; severity: 'success' | 'error' } | null;
type AuditTab = 'schedule' | 'sessions';

// ── Zero-state fallback (used when summary fetch fails or no cycle is active) ─

const EMPTY_SUMMARY: AuditSummary = {
  total_tracked: 0,
  total_due: 0,
  total_overdue: 0,
  by_class: {
    A: { total: 0, due: 0, overdue: 0 },
    B: { total: 0, due: 0, overdue: 0 },
    C: { total: 0, due: 0, overdue: 0 },
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────

/** Default cycle name: "Month Year" (e.g. "June 2026") */
function defaultCycleName(): string {
  return new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

/** Format an ISO date as "Mon D, YYYY" */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Clamp week number to 1–4 per spec */
function weekDisplay(weeksElapsed: number): number {
  return Math.min(4, Math.max(1, Math.ceil(weeksElapsed)));
}

/** Days overdue — guarded against negative values */
function daysOverdue(nextDue: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(nextDue).getTime()) / 86400000));
}

/** Human-readable location label */
function locationLabel(loc: string): string {
  if (loc === 'storeroom') return 'Store Room';
  if (loc === 'working') return 'Working Inventory';
  return loc;
}

const CLASS_META: Record<'A' | 'B' | 'C', { cadence: string; color: string }> = {
  A: { cadence: 'Monthly (30d)', color: '#c62828' },
  B: { cadence: 'Quarterly (90d)', color: '#e65100' },
  C: { cadence: 'Semi-annual (180d)', color: '#1565c0' },
};

// ── Component ──────────────────────────────────────────────────────────────

export const OsAuditDashboard: React.FC = () => {
  const navigate = useNavigate();

  // ── Count Sessions tab state (MADE-293) ───────────────────────────────────
  const [activeTab, setActiveTab] = useState<AuditTab>('schedule');
  const [selectedSession, setSelectedSession] = useState<CountSession | null>(null);
  const [allSessions, setAllSessions] = useState<CountSession[]>([]);

  // Stable callback so OsCountSessionList's fetch effect does not re-fire on every
  // parent render. Without useCallback, the inline arrow changes reference each
  // render and the child's effect dep [onSessionsLoaded] triggers a refetch loop.
  const handleSessionsLoaded = useCallback((loaded: CountSession[]) => {
    setAllSessions(loaded);
  }, []); // no deps — setter identity is stable

  // ── State ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [activeCycle, setActiveCycle] = useState<AuditCycle | null>(null);
  const [summary, setSummary] = useState<AuditSummary>(EMPTY_SUMMARY);
  const [overdueItems, setOverdueItems] = useState<ScheduleItem[]>([]);
  const [weeklyItemCount, setWeeklyItemCount] = useState(0);

  // No-cycle start form
  const [cycleName, setCycleName] = useState(defaultCycleName);
  const [starting, setStarting] = useState(false);

  // Close-cycle confirmation dialog
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  // Toast
  const [toast, setToast] = useState<ToastState>(null);

  // ── Data fetching ────────────────────────────────────────────────────────

  const fetchActiveCycle = useCallback(async (): Promise<AuditCycle | null> => {
    const res = await authFetch('/api/os/audit/cycles/active');
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Unexpected response ${res.status} from /cycles/active`);
    return res.json();
  }, []);

  const fetchSummary = useCallback(async (): Promise<AuditSummary> => {
    try {
      const res = await authFetch('/api/os/audit/summary');
      if (!res.ok) throw new Error(`Failed to load audit summary (${res.status})`);
      const data = await res.json();
      return data as AuditSummary;
    } catch (err) {
      console.error('[OsAuditDashboard] fetchSummary failed — falling back to zeros:', err);
      return EMPTY_SUMMARY;
    }
  }, []);

  const fetchOverdueItems = useCallback(async (): Promise<ScheduleItem[]> => {
    // ?status=overdue filters to genuinely overdue items only (next_due in the past).
    // Client-side guard additionally excludes any future-due items that slip through.
    const res = await authFetch('/api/os/audit/schedule?status=overdue');
    if (!res.ok) throw new Error(`Failed to load schedule (${res.status})`);
    const data = await res.json();
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const items: ScheduleItem[] = Array.isArray(data) ? data : (data.items ?? []);
    return items
      .filter(item => new Date(item.next_due).toISOString().slice(0, 10) < today)
      .slice(0, 10);
  }, []);

  const fetchWeeklyCount = useCallback(async (): Promise<number> => {
    try {
      const res = await authFetch('/api/os/audit/schedule/week');
      if (!res.ok) return 0;
      const data = await res.json();
      const items = Array.isArray(data) ? data : (data.items ?? []);
      return items.length;
    } catch {
      return 0;
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const cycle = await fetchActiveCycle();
      setActiveCycle(cycle);

      if (cycle) {
        const [sum, overdue, weekCount] = await Promise.all([fetchSummary(), fetchOverdueItems(), fetchWeeklyCount()]);
        setSummary(sum);
        setOverdueItems(overdue);
        setWeeklyItemCount(weekCount);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load audit data';
      setToast({ message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [fetchActiveCycle, fetchSummary, fetchOverdueItems, fetchWeeklyCount]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleStartCycle = async () => {
    setStarting(true);
    try {
      const res = await authFetch('/api/os/audit/cycles', {
        method: 'POST',
        body: JSON.stringify({ name: cycleName.trim() || defaultCycleName() }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(errData.error ?? `Start failed (${res.status})`);
      }
      setStarting(false);
      await loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start cycle';
      setToast({ message, severity: 'error' });
    } finally {
      setStarting(false);
    }
  };

  const handleCloseCycle = async () => {
    if (!activeCycle) return;
    setClosing(true);
    try {
      const res = await authFetch(`/api/os/audit/cycles/${activeCycle.id}/close`, {
        method: 'PUT',
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(errData.error ?? `Close failed (${res.status})`);
      }
      setActiveCycle(null);
      setOverdueItems([]);
      setSummary(EMPTY_SUMMARY);
      setCloseDialogOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to close cycle';
      setToast({ message, severity: 'error' });
    } finally {
      setClosing(false);
    }
  };

  // ── Coverage ring value ───────────────────────────────────────────────────
  // Denominator: summary.total_tracked when available, else cycle progress.total_tracked.
  const totalTracked =
    (summary.total_tracked > 0 ? summary.total_tracked : activeCycle?.progress.total_tracked) ?? 0;
  const coveragePercent = activeCycle
    ? Math.round((activeCycle.progress.counted_this_cycle / Math.max(totalTracked, 1)) * 100)
    : 0;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box data-testid="audit-dashboard">
      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => { setActiveTab(v as AuditTab); setSelectedSession(null); }}
          aria-label="Audit dashboard tabs"
        >
          <Tab label="Due Schedule" value="schedule" data-testid="tab-due-schedule" />
          <Tab label="Count Sessions" value="sessions" data-testid="tab-count-sessions" />
        </Tabs>
      </Box>

      {/* ── Count Sessions tab ───────────────────────────────────────────── */}
      {activeTab === 'sessions' && (
        <Box data-testid="count-sessions-tab">
          {!selectedSession ? (
            <OsCountSessionList
              onReview={(s) => setSelectedSession(s)}
              onSessionsLoaded={handleSessionsLoaded}
            />
          ) : (
            <OsCountSessionDetail
              session={selectedSession}
              allSessions={allSessions}
              onBack={() => setSelectedSession(null)}
              onSessionClosed={() => setSelectedSession(null)}
            />
          )}
        </Box>
      )}

      {/* ── Due Schedule tab ─────────────────────────────────────────────── */}
      {activeTab === 'schedule' && (
        <>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          )}
          {!loading && (
            <>
              {/* ── State 1: No Active Cycle ─────────────────────────────────────── */}
      {!activeCycle && (
        <Box maxWidth={520} mx="auto" mt={4}>
          <Card elevation={2}>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={3} alignItems="center" textAlign="center">
                <AssignmentTurnedInIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                <Typography variant="h6" fontWeight={600}>
                  No active inventory cycle
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start a monthly cycle to begin cycle counting. The system will generate
                  a weekly count sheet from your most overdue items.
                </Typography>
                <TextField
                  label="Cycle name"
                  value={cycleName}
                  onChange={(e) => setCycleName(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={starting}
                />
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={starting || !cycleName.trim()}
                  onClick={handleStartCycle}
                  startIcon={
                    starting ? <CircularProgress size={18} color="inherit" /> : undefined
                  }
                >
                  {starting ? 'Starting…' : 'Start Cycle'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* ── State 2: Active Cycle ────────────────────────────────────────── */}
      {activeCycle && (
        <Stack spacing={3}>
          {/* 2a. Cycle Header Bar */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" fontWeight={700}>
                  {activeCycle.name}
                </Typography>
                <Typography color="text.secondary">·</Typography>
                <Typography color="text.secondary">
                  Week {weekDisplay(activeCycle.progress.weeks_elapsed)} of 4
                </Typography>
                <Typography color="text.secondary">·</Typography>
                <Typography color="text.secondary">
                  Started {formatDate(activeCycle.started_at)}
                </Typography>
              </Stack>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setCloseDialogOpen(true)}
              >
                Close Cycle
              </Button>
            </Stack>
          </Paper>

          {/* 2b. Coverage Ring + Stats Row */}
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={4}
              alignItems="center"
              justifyContent="center"
            >
              {/* Coverage ring */}
              <Box position="relative" display="inline-flex" flexShrink={0}>
                <CircularProgress
                  variant="determinate"
                  value={coveragePercent}
                  size={120}
                  thickness={6}
                />
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  bottom={0}
                  right={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                >
                  <Typography variant="h6" fontWeight={700} lineHeight={1.1}>
                    {coveragePercent}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    done
                  </Typography>
                </Box>
              </Box>

              {/* Stats chips */}
              <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
                {[
                  { label: 'Due this week', value: weeklyItemCount },
                  {
                    label: 'Overdue',
                    value: activeCycle.progress.overdue,
                    highlight: activeCycle.progress.overdue > 0,
                  },
                  {
                    label: 'Counted this cycle',
                    value: activeCycle.progress.counted_this_cycle,
                  },
                  { label: 'Total tracked', value: totalTracked },
                ].map(({ label, value, highlight }) => (
                  <Paper
                    key={label}
                    variant="outlined"
                    sx={{ px: 2, py: 1.5, textAlign: 'center', minWidth: 110 }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={highlight ? { color: 'error.main' } : undefined}
                    >
                      {value ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          </Paper>

          {/* 2c. Class Breakdown Cards (A / B / C) */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {(['A', 'B', 'C'] as const).map((cls) => {
              const meta = CLASS_META[cls];
              const stats = summary.by_class[cls];
              return (
                <Card key={cls} elevation={1} sx={{ flex: 1 }}>
                  <CardContent>
                    <Stack spacing={0.75}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={cls}
                          size="small"
                          sx={{
                            bgcolor: meta.color,
                            color: 'white',
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {meta.cadence}
                        </Typography>
                      </Stack>
                      <Typography variant="body2">{stats.total} tracked</Typography>
                      <Typography variant="body2">
                        {stats.due > 0 ? `${stats.due} due` : '— due'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={stats.overdue > 0 ? { color: 'error.main' } : undefined}
                      >
                        {stats.overdue > 0 ? `${stats.overdue} overdue` : '— overdue'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>

          {/* 2d. Overdue Items Preview */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
              Most overdue items
            </Typography>
            <Divider sx={{ mb: 1 }} />

            {overdueItems.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                py={2}
                textAlign="center"
              >
                No overdue items.
              </Typography>
            ) : (
              <Stack divider={<Divider />} spacing={0}>
                {overdueItems.map((item) => {
                  const days = daysOverdue(item.next_due);
                  return (
                    <Stack
                      key={`${item.material_id}-${item.location}`}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      py={1}
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ minWidth: 140 }}
                      >
                        {item.material_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ·
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {locationLabel(item.location)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ·
                      </Typography>
                      <Chip
                        label={item.audit_class}
                        size="small"
                        sx={{
                          bgcolor: CLASS_META[item.audit_class]?.color ?? '#555',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: 11,
                          height: 20,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        ·
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={days > 0 ? { color: 'error.main' } : { color: 'text.secondary' }}
                      >
                        {days > 0 ? `${days} days overdue` : 'Due today'}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            )}

            <Divider sx={{ mt: 1, mb: 2 }} />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              {/* Disabled placeholder — destination added when OS-INV-24 lands */}
              <Button variant="text" size="small" disabled>
                View full schedule
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/os/count-sheet')}
              >
                Generate This Week's Sheet →
              </Button>
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* ── Close Cycle Confirmation Dialog ───────────────────────────────── */}
      <Dialog
        open={closeDialogOpen}
        onClose={() => !closing && setCloseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Close {activeCycle?.name}?</DialogTitle>
        <DialogContent>
          <Typography>
            Close the {activeCycle?.name} cycle? This cannot be undone. Any uncounted
            items will remain in the schedule for the next cycle.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)} disabled={closing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCloseCycle}
            disabled={closing}
            startIcon={
              closing ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {closing ? 'Closing…' : 'Close Cycle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
            </>
          )}
        </>
      )}
    </Box>
  );
};
